import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
} from '@/shared/components';
import type { OtaBookingRefundCategory, OtaBookingRefundLedger } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';
import { majorFromMinor } from '@/shared/lib/money';

export type OtaRefundDialogMode = 'refund' | 'compensation';

type OtaRefundDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: OtaRefundDialogMode;
  ledger: OtaBookingRefundLedger;
  amount: string;
  category: OtaBookingRefundCategory;
  reason: string;
  pending: boolean;
  canSubmit: boolean;
  onAmountChange: (amount: string) => void;
  onCategoryChange: (category: OtaBookingRefundCategory) => void;
  onReasonChange: (reason: string) => void;
  onSubmit: () => void;
};

export function OtaRefundDialog({
  open,
  onOpenChange,
  mode,
  ledger,
  amount,
  category,
  reason,
  pending,
  canSubmit,
  onAmountChange,
  onCategoryChange,
  onReasonChange,
  onSubmit,
}: OtaRefundDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showClose={!pending}>
        <DialogHeader>
          <DialogTitle>
            {mode === 'compensation' ? 'Fehleintrag kompensieren' : 'OTA-Erstattung dokumentieren'}
          </DialogTitle>
          <DialogDescription>
            Der Vorgang dokumentiert einen bereits extern angeordneten Betrag und führt keinen
            Vendor- oder Stripe-Call aus.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {mode === 'refund' && (
            <>
              <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                Betrag
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={amount}
                  disabled={pending}
                  onChange={(event) => onAmountChange(event.target.value)}
                />
              </label>
              <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                Kategorie
                <Select
                  value={category}
                  disabled={pending}
                  onChange={(event) =>
                    onCategoryChange(event.target.value as OtaBookingRefundCategory)
                  }
                >
                  <option value="GuestComplaint">Gästebeschwerde</option>
                  <option value="Defect">Mangel</option>
                </Select>
              </label>
              <p className="text-ink-faint text-xs">
                Verbleibend:{' '}
                {formatMoney(
                  majorFromMinor(ledger.remainingGrossMinor, ledger.currency),
                  ledger.currency,
                )}
              </p>
            </>
          )}
          <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
            Begründung
            <Input
              value={reason}
              maxLength={500}
              disabled={pending}
              onChange={(event) => onReasonChange(event.target.value)}
            />
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            variant={mode === 'compensation' ? 'destructive' : 'brand'}
            loading={pending}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {mode === 'compensation' ? 'Kompensation erfassen' : 'Erstattung erfassen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
